import type {
  HandoverFailureCause,
  HandoverOutcome,
  HandoverType,
  HandoverPickUpState,
  HandoverResolution,
  HandoverTile,
} from "./types";
import type { ReportFilterOption } from "@/features/reports/common/types";

import { AI_ASSISTANT } from "@/config/constants";
import { REPORT_FILTER_ANY } from "@/features/reports/common/constants/list-filters";
import { SLA_CLOCK_COPY } from "@/features/sla/constants";
import { SLA_CLOCK } from "@/types/workspace";

const ASSISTANT = AI_ASSISTANT.name;

/** Most common first, the order the filter and the breakdown read in. */
export const HANDOVER_TYPE_ORDER: HandoverType[] = [
  "human_required",
  "approval",
  "question",
  "draft",
  "knowledge_gap",
  "failed",
];

export const HANDOVER_TYPE_LABEL: Record<HandoverType, string> = {
  human_required: "Needed a person",
  knowledge_gap: "Had a knowledge gap",
  failed: `${ASSISTANT} failed`,
  approval: "Needed approval",
  question: "Had a question",
  draft: "Had a draft",
};

export const HANDOVER_TYPE_FILTER_LABEL = `Why ${ASSISTANT} stopped`;

/** The history drawer's reasons; it leaves drafts out, as the list does. */
export const HANDOVER_HISTORY_TYPE_OPTIONS: ReportFilterOption[] = [
  { key: REPORT_FILTER_ANY, label: "Any reason" },
  ...HANDOVER_TYPE_ORDER.filter((type) => type !== "draft").map((type) => ({
    key: type,
    label: HANDOVER_TYPE_LABEL[type],
  })),
];

/** How a handover ended, as the history drawer's filter names it. */
export const HANDOVER_OUTCOME_FILTERS: ReportFilterOption<HandoverOutcome>[] = [
  { key: "picked_up", label: "Picked up" },
  { key: "in_time", label: "In time" },
  { key: "late", label: "Picked up late" },
  { key: "waiting_late", label: "Still waiting past the target" },
];

export const HANDOVER_OUTCOME_OPTIONS: ReportFilterOption[] = [
  { key: REPORT_FILTER_ANY, label: "Any outcome" },
  ...HANDOVER_OUTCOME_FILTERS,
];

/** The same, while it still waits: the live list speaks in the present. */
export const HANDOVER_WAITING_LABEL: Record<HandoverType, string> = {
  human_required: "Needs a person",
  knowledge_gap: "Has a knowledge gap",
  failed: `${ASSISTANT} failed`,
  approval: "Needs approval",
  question: "Has a question",
  draft: "Draft to review",
};

/** What the row says when the assistant left no words of its own. */
export const HANDOVER_TYPE_FALLBACK: Record<
  Exclude<HandoverType, "failed">,
  string
> = {
  human_required: `${ASSISTANT} asked for a person`,
  knowledge_gap: `${ASSISTANT} didn't know the answer`,
  approval: `${ASSISTANT} needs an approval`,
  question: `${ASSISTANT} has a question`,
  draft: `${ASSISTANT} wrote a draft to review`,
};

/** A failed run's own line describes a reply nobody received, so the cause speaks instead. */
export const HANDOVER_FAILURE_COPY: Record<HandoverFailureCause, string> = {
  model_error: `${ASSISTANT} couldn't write a reply`,
  delivery_failed: "The reply didn't reach them",
  no_card: "The draft couldn't be shown",
  claim_failed: `${ASSISTANT} lost track of who was replying`,
  delivery_interrupted: "Sending stopped part way",
  run_interrupted: `${ASSISTANT} stopped part way`,
  resume_failed: `${ASSISTANT} couldn't pick up after an approval`,
  tool_call_failure: `An action ${ASSISTANT} tried failed`,
};

export const HANDOVER_FAILURE_FALLBACK = `${ASSISTANT} ran into a problem`;

export const TARGET_NOT_CONFIGURED = "Not configured";

/** The inbox SLA clock the page judges against, as SLA settings names it. */
export const TARGET_NAME = `${SLA_CLOCK_COPY[SLA_CLOCK.AI_WAITING].label} target`;

export const TARGET_ALERT_COPY = {
  manager: `Waiting times still show in business hours. Set the ${TARGET_NAME} in SLA settings to see which waits ran late.`,
  member: `Waiting times still show in business hours. Ask an admin to set the ${TARGET_NAME} in SLA settings to see which waits ran late.`,
};

export const AGAINST_TARGET_COPY = {
  notConfiguredManager: `Set the ${TARGET_NAME} to score these waits.`,
  notConfiguredMember: `An admin can set the ${TARGET_NAME} in SLA settings.`,
};

export const HANDOVER_TILE_ORDER: HandoverTile[] = [
  "waiting",
  "longest",
  "beyond",
  "within",
];

export const HANDOVER_TILE_LABEL: Record<HandoverTile, string> = {
  waiting: "Waiting now",
  longest: "Longest wait",
  beyond: "Beyond target",
  within: "Within target",
};

/** A history list opens newest first. */
export const HANDOVER_HISTORY_DEFAULT_SORT = {
  column: "startedAt",
  direction: "descending",
} as const;

/** The slow-tail line opens the picked-up list on the longest wait. */
export const HANDOVER_SLOWEST_SORT = {
  column: "waited",
  direction: "descending",
} as const;

/** The slow-tail line opens this list. */
export const HANDOVER_SLOWEST_TARGET = {
  title: "Slowest pick-ups",
  outcome: "picked_up",
  sort: HANDOVER_SLOWEST_SORT,
} as const;

/** The waiting list opens on the longest wait. */
export const HANDOVER_WAITING_DEFAULT_SORT = {
  column: "wait",
  direction: "descending",
} as const;

/** Past the target is late; inside its last stretch is a warning. */
export const WAIT_NOTE_CLASS = {
  over: "!text-danger",
  due: "!text-warning",
} as const;

/** From this share of the target a waiting row warns "Due in", matching the SLA reminder default. */
export const HANDOVER_REMIND_AT = 0.8;

/** The waiting list is live; it and the tiles refresh this often. */
export const HANDOVERS_LIVE_REFRESH_MS = 60_000;

export const HANDOVERS_SKELETON_ROWS = 6;

/** The history drawer is taller than the card, so it holds more placeholder rows. */
export const HANDOVER_HISTORY_SKELETON_ROWS = 8;

/** The run drawer's second step: how the handover ended. */
export const HANDOVER_PICK_UP_ICON: Record<
  HandoverPickUpState,
  { icon: string; className: string }
> = {
  answered: {
    icon: "solar:user-check-rounded-linear",
    className: "text-muted",
  },
  waiting: { icon: "solar:clock-circle-linear", className: "text-warning" },
  dropped: { icon: "solar:close-circle-linear", className: "text-muted" },
};

/** The thread's bubbles while it loads: the customer left, the team right. */
export const HANDOVER_THREAD_SKELETON = [
  "w-2/3",
  "ml-auto w-1/2",
  "w-3/5",
  "ml-auto w-2/5",
  "w-1/2",
  "ml-auto w-3/5",
];

export const HANDOVERS_PAGE_TITLE = `${ASSISTANT} handovers`;

export const HANDOVER_PICK_UP_TITLE = "Time to pick up";
export const HANDOVER_STOP_REASONS_TITLE = `Why ${ASSISTANT} stopped`;

export const HANDOVERS_CARD = {
  PICK_UP: "handovers_pick_up",
  STOP_REASONS: "handovers_stop_reasons",
  FIGURES: "handovers_figures",
  WAITING: "handovers_waiting",
} as const;

export const HANDOVERS_REPORT_PATH = "/reports/handovers";

/** The open drawer, by contact id. */
export const HANDOVER_DRAWER_PARAM = "handover";

/** What a person did to pick a handover up, as the drawer's sentence says it. */
export const HANDOVER_RESOLUTION_VERB: Record<HandoverResolution, string> = {
  approved: "approved it",
  denied: "denied it",
  replied: "replied",
  taken_over: "took it over",
  resolved: "marked it resolved",
};

/** The same, as the list's short label under the name. */
export const HANDOVER_RESOLUTION_LABEL: Record<HandoverResolution, string> = {
  approved: "Approved",
  denied: "Denied",
  replied: "Replied",
  taken_over: "Took over",
  resolved: "Marked resolved",
};

export const HANDOVER_DROPPED = `${ASSISTANT} dropped it`;
export const HANDOVER_STILL_WAITING = "Still waiting";
export const PICKED_UP_BY_UNKNOWN = "Not recorded";
